/**
 * Report Hooks
 * React Query hooks for report generation
 */

import { useQuery, useMutation } from '@tanstack/react-query'
import { reportsApi } from '@/api'
import type { ReportGenerateRequest } from '@/types/report'

// Query keys
export const reportKeys = {
  all: ['reports'] as const,
  types: () => [...reportKeys.all, 'types'] as const,
  generated: (params: ReportGenerateRequest) => [...reportKeys.all, 'generated', params] as const,
}

/**
 * Get available report types
 */
export function useReportTypes() {
  return useQuery({
    queryKey: reportKeys.types(),
    queryFn: () => reportsApi.getTypes(),
    staleTime: Infinity, // Report types don't change often
  })
}

/**
 * Generate report
 */
export function useGenerateReport() {
  return useMutation({
    mutationFn: (request: ReportGenerateRequest) => reportsApi.generate(request),
  })
}

/**
 * Get dashboard statistics
 */
export function useDashboardStats() {
  return useQuery({
    queryKey: [...reportKeys.all, 'dashboard', 'stats'],
    queryFn: () => reportsApi.getDashboardStats(),
    refetchInterval: 60000, // Refetch every minute
  })
}

/**
 * Get dashboard charts data
 */
export function useDashboardCharts() {
  return useQuery({
    queryKey: [...reportKeys.all, 'dashboard', 'charts'],
    queryFn: () => reportsApi.getDashboardCharts(),
    refetchInterval: 300000, // Refetch every 5 minutes
  })
}
